<template>
  <div class="page-container">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>{{ $t('vip.auditLogsTitle') }}</span>
          <div class="header-actions-wrapper">
            <div class="header-actions">
              <el-button 
                @click="handleRefresh"
                :icon="Refresh"
              >
                {{ $t('common.refresh') }}
              </el-button>
            </div>
            <el-button-group class="view-toggle">
              <el-button 
                :type="viewMode === 'list' ? 'primary' : ''"
                @click="viewMode = 'list'"
              >
                <el-icon><List /></el-icon>
              </el-button>
              <el-button 
                :type="viewMode === 'card' ? 'primary' : ''"
                @click="viewMode = 'card'"
              >
                <el-icon><Grid /></el-icon>
              </el-button>
            </el-button-group>
          </div>
        </div>
      </template>

      <!-- Filters -->
      <div class="filter-container">
        <el-form :model="filterForm" class="filter-form">
          <!-- Row 1: Date Range (full width) -->
          <div class="filter-row filter-row-date">
            <div class="filter-date-section">
              <label class="filter-label">{{ $t('common.dateRange') }}</label>
              <QuickDateRangePicker v-model="dateRange" @change="handleDateChange" />
            </div>
          </div>

          <!-- Row 2: Search, Operation Type, Entity Type -->
          <div class="filter-row">
            <div class="filter-group filter-group-search">
              <label class="filter-label">{{ $t('common.search') }}</label>
              <el-input
                v-model="searchQuery"
                :placeholder="$t('vip.searchPlaceholder')"
                clearable
                @clear="handleLocalSearch"
                @input="handleLocalSearch"
              >
                <template #prefix>
                  <el-icon><Search /></el-icon>
                </template>
              </el-input>
            </div>

            <div class="filter-group">
              <label class="filter-label">{{ $t('vip.operationType') }}</label>
              <el-select
                v-model="filterForm.operationType"
                :placeholder="$t('vip.allTypes')"
                clearable
                @change="handleLocalSearch"
              >
                <el-option :label="$t('vip.created')" value="created" />
                <el-option :label="$t('vip.vipPurchase')" value="VIP_PURCHASE" />
                <el-option :label="$t('vip.cancel')" value="cancel" />
                <el-option :label="$t('vip.change')" value="change" />
              </el-select>
            </div>

            <div class="filter-group">
              <label class="filter-label">{{ $t('vip.entityType') }}</label>
              <el-select
                v-model="filterForm.relatedEntityType"
                :placeholder="$t('vip.allEntities')"
                clearable
                @change="handleFilterChange"
              >
                <el-option :label="$t('vip.package')" value="Package" />
                <el-option :label="$t('vip.purchase')" value="Purchase" />
                <el-option :label="$t('vip.customer')" value="Customer" />
              </el-select>
            </div>
          </div>

          <!-- Row 3: Related Entity ID, Category, Operator ID, Actions -->
          <div class="filter-row">
            <div class="filter-group">
              <label class="filter-label">{{ $t('vip.relatedEntityId') }}</label>
              <el-input-number
                v-model="filterForm.relatedEntityId"
                :placeholder="$t('vip.enterRelatedEntityId')"
                :min="0"
                controls-position="right"
                clearable
                @clear="handleFilterChange"
                @change="handleFilterChange"
              />
            </div>

            <div class="filter-group">
              <label class="filter-label">{{ $t('vip.category') }}</label>
              <el-input-number
                v-model="filterForm.category"
                :placeholder="$t('vip.enterCategory')"
                :min="0"
                controls-position="right"
                clearable
                @clear="handleFilterChange"
                @change="handleFilterChange"
              />
            </div>

            <div class="filter-group">
              <label class="filter-label">{{ $t('vip.operatorId') }}</label>
              <el-input-number
                v-model="filterForm.operatorId"
                :placeholder="$t('vip.enterOperatorId')"
                :min="0"
                controls-position="right"
                clearable
                @clear="handleFilterChange"
                @change="handleFilterChange"
              />
            </div>

            <div class="filter-group filter-actions">
              <el-button type="primary" @click="handleFilterChange" :icon="Search">
                {{ $t('common.search') }}
              </el-button>
              <el-button @click="handleReset" :icon="Refresh">
                {{ $t('common.reset') }}
              </el-button>
            </div>
          </div>
        </el-form>
      </div>

      <!-- List View (Table) -->
      <div v-if="viewMode === 'list'" class="table-container">
        <el-table
          v-loading="loading"
          :data="filteredAuditLogs"
          stripe
          style="width: 100%;"
          :max-height="600"
        >
          <el-table-column type="index" label="#" width="60" align="center">
            <template #default="{ $index }">
              {{ (pagination.page - 1) * pagination.pageSize + $index + 1 }}
            </template>
          </el-table-column>
          
          <el-table-column :label="$t('vip.operator')" min-width="180" show-overflow-tooltip>
            <template #default="{ row }">
              <div class="operator-cell">
                <i v-if="row.operatorRole === 'Customer'" class="iconfont icon-helmet operator-icon-customer"></i>
                <i v-else class="iconfont icon-teacher operator-icon-admin"></i>
                <div class="operator-info">
                  <span class="operator-name">{{ row.operatorName || `Operator ${row.operatorId}` }}</span>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="operationType" :label="$t('vip.operationType')" width="180" align="center">
            <template #default="{ row }">
              <el-tag :type="getOperationTypeTag(row.operationType)" size="small" effect="plain">
                {{ row.operationTypeDisplay }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column prop="operationTime" :label="$t('vip.operationTime')" width="180" align="center">
            <template #default="{ row }">
              {{ formatDateTime(row.operationTime || row.createdAt) }}
            </template>
          </el-table-column>

          <el-table-column prop="success" :label="$t('vip.status')" width="140" align="center">
            <template #default="{ row }">
              <div class="status-cell">
                <el-icon v-if="row.success" class="status-icon-success"><CircleCheck /></el-icon>
                <el-icon v-else class="status-icon-error"><CircleClose /></el-icon>
                <span class="status-text">{{ row.success ? $t('common.success') : $t('common.error') }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column :label="$t('vip.details')" min-width="350">
            <template #default="{ row }">
              <div class="details-cell">
                <div v-if="row.reason" class="reason-content">
                  <el-icon class="reason-icon"><InfoFilled /></el-icon>
                  <span class="reason-text">{{ row.reason }}</span>
                </div>
                <div v-else-if="row.errorMessage" class="error-text">
                  <el-icon><Warning /></el-icon>
                  <span>{{ row.errorMessage }}</span>
                </div>
                <span v-else class="empty-text">-</span>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- Card View (Mobile) -->
      <div v-else class="audit-cards" v-loading="loading">
        <div v-if="filteredAuditLogs.length === 0" class="empty-state">
          <el-empty :description="$t('vip.noAuditLogs')" />
        </div>
        <div v-else class="cards-grid">
          <div 
            v-for="(log, index) in filteredAuditLogs" 
            :key="log.id || index" 
            class="audit-card"
          >
            <div class="card-header-row">
              <div class="card-operator">
                <i v-if="log.operatorRole === 'Customer'" class="iconfont icon-helmet operator-icon-customer"></i>
                <i v-else class="iconfont icon-teacher operator-icon-admin"></i>
                <div class="operator-details">
                  <span class="operator-name">{{ log.operatorName || `Operator ${log.operatorId}` }}</span>
                </div>
              </div>
              <div class="card-status">
                <el-icon v-if="log.success" class="status-icon-success"><CircleCheck /></el-icon>
                <el-icon v-else class="status-icon-error"><CircleClose /></el-icon>
                <span class="status-text">{{ log.success ? $t('common.success') : $t('common.error') }}</span>
              </div>
            </div>
            
            <div class="card-body">
              <div class="card-info-row">
                <div class="info-item">
                  <el-icon><Clock /></el-icon>
                  <span class="label">{{ $t('vip.operationTime') }}:</span>
                  <span class="value">{{ formatDateTime(log.operationTime || log.createdAt) }}</span>
                </div>
              </div>
              
              <div class="card-info-row">
                <div class="info-item">
                  <el-icon><Operation /></el-icon>
                  <span class="label">{{ $t('vip.operationType') }}:</span>
                  <el-tag :type="getOperationTypeTag(log.operationType)" size="small" effect="plain">
                    {{ log.operationTypeDisplay }}
                  </el-tag>
                </div>
              </div>
              
              <div class="card-details" v-if="log.reason || log.errorMessage">
                <div class="details-label">{{ $t('vip.details') }}:</div>
                <div v-if="log.reason" class="details-content reason-content">
                  <el-icon class="reason-icon"><InfoFilled /></el-icon>
                  <span>{{ log.reason }}</span>
                </div>
                <div v-else-if="log.errorMessage" class="details-content error-text">
                  <el-icon><Warning /></el-icon>
                  <span>{{ log.errorMessage }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <el-pagination
        v-if="total > 0 || filteredAuditLogs.length > 0"
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="total || filteredAuditLogs.length"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
        style="margin-top: 20px; justify-content: flex-end;"
      />

      <!-- Empty State -->
      <el-empty
        v-if="!loading && filteredAuditLogs.length === 0"
        :description="$t('vip.noAuditLogs')"
      />
    </el-card>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVipStore } from '@/stores/vip'
import { Search, Refresh, Document, Check, Warning, User, Close, InfoFilled, CircleCheck, CircleClose, List, Grid, Clock, Operation } from '@element-plus/icons-vue'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import MTime from '@/utils/time-handler'
import QuickDateRangePicker from '@/components/QuickDateRangePicker.vue'

dayjs.extend(utc)

const { t } = useI18n()
const vipStore = useVipStore()

// State
const loading = ref(false)
const auditLogs = ref([])
const total = ref(0)
const viewMode = ref('list')
const searchQuery = ref('') // Local search query

// Initialize date range with today
const today = MTime.getCurrentDate()
const dateRange = ref([today, today])

// Filter form - initialize with today's date using new server-side format
const filterForm = ref({
  operationType: '',
  relatedEntityType: '',
  relatedEntityId: null,
  category: null,
  operatorId: null,
  success: null,
  startDate: MTime.formatQueryStartDate(today),
  endDate: MTime.formatQueryEndDate(today)
})

// Map old API operation types to new simple types
const operationTypeMap = {
  'BOOKING_CREATE': 'created',
  'VIP_PURCHASE': 'VIP_PURCHASE',
  'PURCHASE': 'created',
  'BOOKING_CANCEL': 'cancel',
  'BOOKING_CANCEL_HOUR': 'cancel',
  'DELETE': 'cancel',
  'GIFT': 'change',
  'GRANT': 'change',
  'VIP_GRANT': 'change',
  'PRICE_CHANGED': 'change',
  'BOOKING_UPDATE': 'update',
  'UPDATE': 'update',
  'ACTIVATE': 'update',
  'DEACTIVATE': 'update'
}

// Get mapped operation type for filtering
const getMappedOperationType = (type) => {
  return operationTypeMap[type] || type
}

// Computed: Filtered audit logs based on local search and operation type
const filteredAuditLogs = computed(() => {
  let result = auditLogs.value
  
  // First filter by operation type
  if (filterForm.value.operationType && filterForm.value.operationType.trim() !== '') {
    const filterType = filterForm.value.operationType.toLowerCase().trim()
    result = result.filter(log => {
      const mappedType = getMappedOperationType(log.operationType)
      return mappedType === filterType
    })
  }
  
  // Then filter by search query
  if (searchQuery.value && searchQuery.value.trim() !== '') {
    const query = searchQuery.value.toLowerCase().trim()
    
    result = result.filter(log => {
      // Search in operator name
      if (log.operatorName && log.operatorName.toLowerCase().includes(query)) {
        return true
      }
      
      // Search in operator role
      if (log.operatorRole && log.operatorRole.toLowerCase().includes(query)) {
        return true
      }
      
      // Search in operation type display
      if (log.operationTypeDisplay && log.operationTypeDisplay.toLowerCase().includes(query)) {
        return true
      }
      
      // Search in reason/description
      if (log.reason && log.reason.toLowerCase().includes(query)) {
        return true
      }
      
      // Search in error message
      if (log.errorMessage && log.errorMessage.toLowerCase().includes(query)) {
        return true
      }
      
      // Search in order number (extracted from reason)
      if (log.reason) {
        const orderMatch = log.reason.match(/Order:\s*([A-Z0-9]+)/i)
        if (orderMatch && orderMatch[1].toLowerCase().includes(query)) {
          return true
        }
      }
      
      return false
    })
  }
  
  return result
})

// Pagination
const pagination = ref({
  page: 1,
  pageSize: 20
})

// Helper function to convert local date to UTC ISO format
const convertToUTC = (dateString) => {
  if (!dateString) return ''
  // Parse as local date and convert to UTC ISO string
  const date = new Date(dateString)
  return date.toISOString()
}

// Format datetime - uses MTime utility for consistent formatting
const formatDateTime = (dateString) => {
  return MTime.formatDateTime(dateString)
}

// Format operation type for display
const formatOperationType = (type) => {
  // First try simple types
  const simpleTypeMap = {
    'created': t('vip.created'),
    'VIP_PURCHASE': t('vip.vipPurchase'),
    'cancel': t('vip.cancel'),
    'change': t('vip.change')
  }
  
  // Fallback for legacy types from API response
  const legacyTypeMap = {
    'VIP_GRANT': t('vip.vipGrant'),
    'PURCHASE': t('vip.purchase'),
    'GIFT': t('vip.gift'),
    'GRANT': t('vip.grant'),
    'PRICE_CHANGED': t('vip.priceChanged'),
    'UPDATE': t('vip.update'),
    'DELETE': t('vip.delete'),
    'ACTIVATE': t('vip.activate'),
    'DEACTIVATE': t('vip.deactivate'),
    'BOOKING_CREATE': t('vip.bookingCreate'),
    'BOOKING_CANCEL_HOUR': t('vip.cancelHour'),
    'BOOKING_UPDATE': t('vip.bookingUpdate'),
    'BOOKING_CANCEL': t('vip.bookingCancel')
  }
  
  return simpleTypeMap[type] || legacyTypeMap[type] || type
}

// Get operation type tag color
const getOperationTypeTag = (type) => {
  // First try simple types
  const simpleTypeMap = {
    'created': 'success',
    'VIP_PURCHASE': 'success',
    'cancel': 'danger',
    'change': 'warning'
  }
  
  // Fallback for legacy types from API response
  const legacyTypeMap = {
    'VIP_GRANT': 'warning',
    'PURCHASE': 'success',
    'GIFT': 'warning',
    'GRANT': 'warning',
    'PRICE_CHANGED': 'warning',
    'UPDATE': 'primary',
    'DELETE': 'danger',
    'ACTIVATE': 'success',
    'DEACTIVATE': 'info',
    'BOOKING_CREATE': 'success',
    'BOOKING_CANCEL_HOUR': 'warning',
    'BOOKING_UPDATE': 'primary',
    'BOOKING_CANCEL': 'danger'
  }
  
  return simpleTypeMap[type] || legacyTypeMap[type] || 'info'
}

// Handle date range change (from QuickDateRangePicker)
const handleDateChange = (apiDates) => {
  if (apiDates && apiDates.startDate && apiDates.endDate) {
    filterForm.value.startDate = apiDates.startDate
    filterForm.value.endDate = apiDates.endDate
  } else {
    filterForm.value.startDate = ''
    filterForm.value.endDate = ''
  }
  handleFilterChange()
}

// Handle filter change
const handleFilterChange = () => {
  pagination.value.page = 1
  loadData()
}

// Handle local search (client-side filtering)
const handleLocalSearch = () => {
  // Local search doesn't trigger API call, just filters displayed data
  // Reset pagination to page 1 when searching
  pagination.value.page = 1
}

// Handle reset
const handleReset = () => {
  // Reset to today's date using new server-side format
  const today = MTime.getCurrentDate()
  filterForm.value = {
    operationType: '',
    relatedEntityType: '',
    relatedEntityId: null,
    category: null,
    operatorId: null,
    success: null,
    startDate: MTime.formatQueryStartDate(today),
    endDate: MTime.formatQueryEndDate(today)
  }
  dateRange.value = [today, today]
  searchQuery.value = '' // Clear local search
  pagination.value.page = 1
  loadData()
}

// Handle refresh
const handleRefresh = () => {
  loadData()
}

// Handle page size change
const handleSizeChange = (size) => {
  pagination.value.pageSize = size
  pagination.value.page = 1
  loadData()
}

// Handle page change
const handlePageChange = (page) => {
  pagination.value.page = page
  loadData()
}

// Load data
const loadData = async () => {
  loading.value = true
  
  try {
    // Prepare params with correct types for API (POST body)
    // Note: operationType is NOT sent to API - it's only used for local filtering
    const params = {
      pageIndex: pagination.value.page - 1, // API uses 0-based indexing
      pageSize: pagination.value.pageSize,
      startDate: filterForm.value.startDate || undefined,
      endDate: filterForm.value.endDate || undefined,
      relatedEntityId: filterForm.value.relatedEntityId !== null ? parseInt(filterForm.value.relatedEntityId) : undefined,
      relatedEntityType: filterForm.value.relatedEntityType || undefined,
      category: filterForm.value.category !== null ? parseInt(filterForm.value.category) : undefined,
      operatorId: filterForm.value.operatorId !== null ? parseInt(filterForm.value.operatorId) : undefined,
      success: filterForm.value.success !== null ? filterForm.value.success : undefined
    }

    // Remove undefined values
    Object.keys(params).forEach(key => {
      if (params[key] === undefined || params[key] === null || params[key] === '') {
        delete params[key]
      }
    })

    console.log('[Audit Logs] Loading data with params:', params)

    // Fetch audit logs
    const res = await vipStore.fetchAuditLogs(params)
    
    if (res.success) {
      // Store already extracts items array from response
      auditLogs.value = res.data || []
      total.value = res.total || 0
      
      console.log('[Audit Logs] Loaded successfully:', {
        items: auditLogs.value.length,
        total: total.value,
        page: res.page,
        pageSize: res.pageSize
      })
    } else {
      console.error('[Audit Logs] Failed to load:', res.message)
    }
  } catch (error) {
    console.error('[Audit Logs] Failed to load audit logs:', error)
  } finally {
    loading.value = false
  }
}

// Watch for store changes
watch(() => vipStore.auditLogs, (newVal) => {
  if (newVal) {
    auditLogs.value = newVal
  }
})

// Initialize on mount
onMounted(() => {
  loadData()
})
</script>

<style lang="scss" scoped>
.page-container {
  padding: 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;

  span {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }

  .header-actions-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .header-actions {
    display: flex;
    gap: 12px;
    align-items: center;
  }

  .view-toggle {
    display: flex;
    
    .el-button {
      padding: 8px 12px;
      
      .el-icon {
        font-size: 18px;
      }
    }
  }
}

.filter-container {
  margin-bottom: 20px;
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  border-radius: 12px;
  border: 1px solid #e4e7ed;
}

.filter-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
  
  @media (min-width: 768px) {
    gap: 12px;
  }
  
  @media (min-width: 1200px) {
    gap: 16px;
  }
  
  @media (min-width: 1400px) {
    gap: 20px;
  }
}

// Date range row (full width)
.filter-row-date {
  .filter-date-section {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 150px;
  max-width: 220px;
  
  @media (min-width: 768px) {
    min-width: 140px;
  }
  
  @media (min-width: 1200px) {
    min-width: 150px;
    max-width: 240px;
  }
  
  @media (min-width: 1400px) {
    max-width: 260px;
  }
}

// Search field wider
.filter-group-search {
  flex: 2;
  min-width: 300px;
  max-width: 500px;
  
  @media (min-width: 768px) {
    min-width: 350px;
  }
  
  @media (min-width: 1200px) {
    min-width: 400px;
    max-width: 600px;
  }
  
  @media (min-width: 1400px) {
    min-width: 450px;
    max-width: 700px;
  }
}

.filter-label {
  font-weight: 500;
  color: #606266;
  font-size: 14px;
  line-height: 1.5;
  white-space: nowrap;
}

.filter-actions {
  flex: 0 0 auto;
  min-width: auto;
  max-width: none;
  display: flex;
  gap: 8px;
  align-items: flex-end;
  padding-bottom: 0;
  
  .el-button {
    white-space: nowrap;
    height: 36px;
  }
}

.filter-container {
  :deep(.el-select),
  :deep(.el-input),
  :deep(.el-date-editor) {
    width: 100%;
    
    .el-input__wrapper {
      box-shadow: 0 0 0 1px #dcdfe6 inset;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      border-radius: 6px;
      background-color: #fff;

      &:hover {
        box-shadow: 0 0 0 1px #c0c4cc inset;
        transform: translateY(-1px);
      }

      &.is-focus {
        box-shadow: 0 0 0 1px #409eff inset;
        background-color: #fff;
      }
    }
  }

  .el-button {
    height: 36px;
    padding: 0 20px;
    border-radius: 6px;
    font-weight: 500;
    transition: all 0.3s;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
    }
  }
}

.stats-row {
  margin-bottom: 20px;
  
  .stat-card {
    border-radius: 8px;
    transition: all 0.3s;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
    
    .stat-content {
      display: flex;
      align-items: center;
      gap: 16px;
      
      .stat-icon {
        width: 48px;
        height: 48px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 24px;
      }
      
      .stat-info {
        flex: 1;
        
        .stat-value {
          font-size: 24px;
          font-weight: 600;
          color: #303133;
          line-height: 1.2;
        }
        
        .stat-label {
          font-size: 13px;
          color: #909399;
          margin-top: 4px;
        }
      }
    }
  }
}

.table-container {
  margin-top: 20px;
  
  :deep(.el-table) {
    border-radius: 8px;
    overflow: hidden;

    .el-table__body-wrapper {
      overflow-y: auto;
      overflow-x: hidden;
      
      /* Custom scrollbar styling */
      &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      
      &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
      }
      
      &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 4px;
        
        &:hover {
          background: #a8a8a8;
        }
      }
    }
    
    .el-table__header th {
      background-color: #f5f7fa;
      color: #606266;
      font-weight: 600;
    }

    .el-table__row:hover {
      background-color: #f5f7fa;
    }
  }
}

.operator-cell {
  display: flex;
  align-items: center;
  gap: 8px;

  .iconfont {
    font-size: 20px;
    flex-shrink: 0;
  }
  
  .operator-icon-customer {
    color: #e6a23c;
  }
  
  .operator-icon-admin {
    color: #409eff;
  }

  .operator-info {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .operator-name {
      font-weight: 500;
      color: #303133;
      font-size: 14px;
    }
  }
}

.status-cell {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 4px 0;

  .status-icon-success,
  .status-icon-error {
    font-size: 20px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }

  .status-icon-success {
    color: #67c23a;
  }

  .status-icon-error {
    color: #f56c6c;
  }

  .status-text {
    display: inline;
    white-space: nowrap;
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
    vertical-align: middle;
  }
}

.details-cell {
  padding: 4px 0;

  .reason-content {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    line-height: 1.6;

    .reason-icon {
      color: #409eff;
      font-size: 16px;
      margin-top: 2px;
      flex-shrink: 0;
    }

    .reason-text {
      color: #303133;
      font-size: 13px;
      word-break: break-word;
    }
  }

  .description-text {
    color: #606266;
    font-size: 13px;
    line-height: 1.6;
  }

  .error-text {
    color: #f56c6c;
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 13px;
    line-height: 1.6;

    .el-icon {
      flex-shrink: 0;
      margin-top: 2px;
    }
  }

  .empty-text {
    color: #c0c4cc;
    font-style: italic;
  }
}

// Card View Styles
.audit-cards {
  margin-top: 20px;
  
  .empty-state {
    padding: 40px 0;
  }
  
  .cards-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    
    @media (min-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }
    
    @media (min-width: 1200px) {
      grid-template-columns: repeat(3, 1fr);
    }
  }
  
  .audit-card {
    background: #fff;
    border-radius: 12px;
    border: 1px solid #e4e7ed;
    overflow: hidden;
    transition: all 0.3s ease;
    
    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      transform: translateY(-2px);
      border-color: #409eff;
    }
    
    .card-header-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px;
      background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
      border-bottom: 1px solid #e4e7ed;
      
      .card-operator {
        display: flex;
        align-items: center;
        gap: 12px;
        flex: 1;
        min-width: 0;
        
        .iconfont {
          font-size: 28px;
          flex-shrink: 0;
        }
        
        .operator-icon-customer {
          color: #e6a23c;
        }
        
        .operator-icon-admin {
          color: #409eff;
        }
        
        .operator-details {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 0;
          
          .operator-name {
            font-weight: 600;
            color: #303133;
            font-size: 14px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
      
      .card-status {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-shrink: 0;
        
        .status-icon-success,
        .status-icon-error {
          font-size: 20px;
          flex-shrink: 0;
        }
        
        .status-icon-success {
          color: #67c23a;
        }
        
        .status-icon-error {
          color: #f56c6c;
        }
        
        .status-text {
          font-size: 13px;
          font-weight: 600;
          white-space: nowrap;
        }
      }
    }
    
    .card-body {
      padding: 16px;
      
      .card-info-row {
        margin-bottom: 12px;
        
        &:last-child {
          margin-bottom: 0;
        }
        
        .info-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          
          .el-icon {
            font-size: 16px;
            color: #909399;
            flex-shrink: 0;
          }
          
          .label {
            color: #606266;
            font-weight: 500;
            white-space: nowrap;
          }
          
          .value {
            color: #303133;
            flex: 1;
          }
        }
      }
      
      .card-details {
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid #e4e7ed;
        
        .details-label {
          font-size: 12px;
          font-weight: 600;
          color: #909399;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .details-content {
          font-size: 13px;
          line-height: 1.6;
          color: #606266;
          
          &.reason-content {
            display: flex;
            align-items: flex-start;
            gap: 6px;
            
            .reason-icon {
              color: #409eff;
              font-size: 16px;
              flex-shrink: 0;
              margin-top: 2px;
            }
          }
          
          &.error-text {
            color: #f56c6c;
            display: flex;
            align-items: flex-start;
            gap: 6px;
            
            .el-icon {
              flex-shrink: 0;
              margin-top: 2px;
            }
          }
        }
      }
    }
  }
}

// Mobile Optimizations
@media (max-width: 768px) {
  .filter-container {
    padding: 12px;
    margin-bottom: 12px;
  }
  
  .filter-form {
    gap: 12px;
  }
  
  .filter-row {
    flex-direction: column;
    gap: 10px;
  }
  
  .filter-label {
    font-size: 13px;
    margin-bottom: 2px;
  }
  
  .filter-group,
  .filter-group-search {
    max-width: 100% !important;
    min-width: 100% !important;
    gap: 6px;
  }
  
  .filter-date-section {
    width: 100% !important;
    gap: 8px;
    
    .filter-label {
      font-size: 14px;
      font-weight: 600;
    }
  }
  
  // Make quick date buttons more compact
  :deep(.quick-selection) {
    .el-button {
      padding: 6px 10px;
      font-size: 12px;
      min-width: auto;
    }
  }
  
  // Make date picker more compact
  :deep(.el-date-editor.el-input__wrapper) {
    min-width: 100% !important;
    
    .el-input__inner {
      font-size: 13px;
    }
  }
  
  // Optimize input fields
  :deep(.el-input),
  :deep(.el-select),
  :deep(.el-input-number) {
    .el-input__wrapper {
      padding: 0 12px;
    }
    
    .el-input__inner {
      font-size: 13px;
    }
  }
  
  .filter-actions {
    width: 100%;
    flex-direction: row;
    gap: 10px;
    margin-top: 4px;
    
    .el-button {
      flex: 1;
      height: 40px;
      font-size: 14px;
      font-weight: 600;
    }
  }
  
  .stats-row {
    .el-col {
      margin-bottom: 12px;
    }
  }
  
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    
    .header-actions-wrapper {
      width: 100%;
      justify-content: space-between;
    }
  }
}
</style>
